/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.core.data.repositoryImpl.cauce

import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.postgrest
import io.github.jan.supabase.postgrest.rpc
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.onStart
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import studio.oddpods.cauce.core.data.repository.cauce.DashboardRepository
import studio.oddpods.cauce.core.data.repository.cauce.PaymentRepository
import studio.oddpods.cauce.core.data.repository.cauce.SessionRepository
import studio.oddpods.cauce.core.data.util.safeCall
import studio.oddpods.cauce.core.data.util.safeThrow
import studio.oddpods.cauce.core.model.payment.CreatePaymentRequest
import studio.oddpods.cauce.core.model.payment.Payment

class PaymentRepositoryImpl(
    private val supabase: SupabaseClient,
    private val sessionRepository: SessionRepository,
    private val dashboardRepository: DashboardRepository,
) : PaymentRepository {

    private val allPayments = MutableStateFlow<List<Payment>?>(null)

    override fun getPaymentsByPatient(patientId: String): Flow<List<Payment>> =
        allPayments
            .onStart { if (allPayments.value == null) fetchAll() }
            .filterNotNull()
            .map { list -> list.filter { it.patientId == patientId } }

    override fun getPaymentsBySession(sessionId: String): Flow<List<Payment>> =
        allPayments
            .onStart { if (allPayments.value == null) fetchAll() }
            .filterNotNull()
            .map { list -> list.filter { it.sessionId == sessionId } }

    override suspend fun createPayment(request: CreatePaymentRequest): Result<Payment> = safeCall {
        val result = supabase.postgrest.rpc(
            "create_payment",
            buildJsonObject {
                put("p_session_id", request.sessionId)
                put("p_patient_id", request.patientId)
                put("p_amount", request.amount)
                put("p_method", request.method)
                put("p_notes", request.notes)
                put("p_received_at", request.receivedAt)
            },
        )
        val payment = result.decodeAs<Payment>()
        fetchAll()
        sessionRepository.refresh().getOrNull()
        dashboardRepository.refresh().getOrNull()
        payment
    }

    override suspend fun refresh(): Result<Unit> = safeCall {
        if (allPayments.value != null) fetchAll()
    }

    private suspend fun fetchAll() = safeThrow {
        allPayments.value = supabase.postgrest.from("payments").select {
            order("received_at", io.github.jan.supabase.postgrest.query.Order.DESCENDING)
        }.decodeList<Payment>()
    }
}
